/*
Purpose: Small status / delivery-mode chips shared by every Calendar (v2) surface.
*/

import React from 'react';
import PropTypes from 'prop-types';

import ApartmentIcon from '@mui/icons-material/Apartment';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import BlockIcon from '@mui/icons-material/Block';
import BoltIcon from '@mui/icons-material/Bolt';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import DesktopWindowsOutlinedIcon from '@mui/icons-material/DesktopWindowsOutlined';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import PauseCircleOutlineIcon from '@mui/icons-material/PauseCircleOutline';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import ReplayIcon from '@mui/icons-material/Replay';
import ScheduleIcon from '@mui/icons-material/Schedule';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined';
import HowToRegOutlinedIcon from '@mui/icons-material/HowToRegOutlined';

import {
  DELIVERY_MODE,
  DELIVERY_MODE_META,
  DISPLAY_STATUS,
  DISPLAY_STATUS_META,
} from '../constants';

const STATUS_ICONS = {
  [DISPLAY_STATUS.PENDING]: ScheduleIcon,
  [DISPLAY_STATUS.ONGOING]: FiberManualRecordIcon,
  [DISPLAY_STATUS.ATTENDED]: CheckIcon,
  [DISPLAY_STATUS.MISSED]: CloseIcon,
  [DISPLAY_STATUS.OFFLINE]: RemoveCircleOutlineIcon,
  [DISPLAY_STATUS.CANCELLED]: BlockIcon,
  [DISPLAY_STATUS.PAUSED]: PauseCircleOutlineIcon,
  [DISPLAY_STATUS.FORFEIT]: RemoveCircleOutlineIcon,
  [DISPLAY_STATUS.WRITEOFF]: RemoveCircleOutlineIcon,
  [DISPLAY_STATUS.DEFER_REQUESTED]: ReplayIcon,
  [DISPLAY_STATUS.SLOT_ASSIGN]: SwapHorizIcon,
  [DISPLAY_STATUS.ONE_OFF_REQUESTED]: AutoAwesomeIcon,
  [DISPLAY_STATUS.DIAGNOSTICS_CLOSED]: BlockIcon,
  [DISPLAY_STATUS.DIAGNOSTICS_COMPLETED]: FactCheckOutlinedIcon,
  [DISPLAY_STATUS.STUDENT_CONVERTED]: HowToRegOutlinedIcon,
};

const MODE_ICONS = {
  [DELIVERY_MODE.IN_CENTRE]: ApartmentIcon,
  [DELIVERY_MODE.ONLINE]: DesktopWindowsOutlinedIcon,
  [DELIVERY_MODE.FLEXI]: BoltIcon,
};

export function statusIconFor(status) {
  return STATUS_ICONS[status] || ScheduleIcon;
}

export function modeIconFor(mode) {
  return MODE_ICONS[mode] || ApartmentIcon;
}

/**
 * A coloured status chip.
 */
export function StatusPill({ status, showLabel, showDot, onClick, disabled, label, title }) {
  const meta = DISPLAY_STATUS_META[status] || DISPLAY_STATUS_META[DISPLAY_STATUS.PENDING];
  const Icon = statusIconFor(status);
  const text = label || meta.label;
  const className = `cv2-status-pill cv2-tone-${meta.tone}`;

  const inner = (
    <>
      {showDot ? <span className="cv2-status-pill__dot" /> : <Icon className="cv2-icon" />}
      {showLabel ? text : null}
    </>
  );

  if (!onClick) {
    return (
      <span className={className} title={title || text}>
        {inner}
      </span>
    );
  }

  return (
    <button
      type="button"
      className={className}
      onClick={onClick}
      disabled={disabled}
      title={title || `${text} — change status`}
    >
      {inner}
    </button>
  );
}

StatusPill.propTypes = {
  status: PropTypes.string.isRequired,
  showLabel: PropTypes.bool,
  showDot: PropTypes.bool,
  onClick: PropTypes.func,
  disabled: PropTypes.bool,
  label: PropTypes.string,
  title: PropTypes.string,
};

StatusPill.defaultProps = {
  showLabel: true,
  showDot: false,
  onClick: undefined,
  disabled: false,
  label: undefined,
  title: undefined,
};

/**
 * A delivery-mode chip (In Centre / Online / Flexi).
 */
export function ModeBadge({ mode, showLabel, onClick, disabled }) {
  const meta = DELIVERY_MODE_META[mode] || DELIVERY_MODE_META[DELIVERY_MODE.IN_CENTRE];
  const Icon = modeIconFor(mode);
  const className = [
    'cv2-mode-badge',
    `cv2-mode-badge--${mode}`,
    showLabel ? '' : 'cv2-mode-badge--icon-only',
  ]
    .filter(Boolean)
    .join(' ');

  const inner = (
    <>
      <Icon className="cv2-icon" />
      {showLabel ? meta.label : null}
    </>
  );

  if (!onClick) {
    return (
      <span className={className} title={meta.label}>
        {inner}
      </span>
    );
  }

  return (
    <button
      type="button"
      className={className}
      onClick={onClick}
      disabled={disabled}
      title={`${meta.label} — change enrolment category`}
    >
      {inner}
    </button>
  );
}

ModeBadge.propTypes = {
  mode: PropTypes.string.isRequired,
  showLabel: PropTypes.bool,
  onClick: PropTypes.func,
  disabled: PropTypes.bool,
};

ModeBadge.defaultProps = {
  showLabel: true,
  onClick: undefined,
  disabled: false,
};

/** Year-level chip, e.g. "Y7". Renders nothing when the level is unknown. */
export function YearChip({ yearLevel }) {
  if (!yearLevel) {
    return null;
  }
  return <span className="cv2-year-chip">{yearLevel}</span>;
}

YearChip.propTypes = {
  yearLevel: PropTypes.string,
};

YearChip.defaultProps = {
  yearLevel: '',
};

/** Small marker tag used for One-off / Deferred / Make-up / Flexi annotations. */
export function Tag({ variant, children }) {
  return <span className={`cv2-tag cv2-tag--${variant}`}>{children}</span>;
}

Tag.propTypes = {
  variant: PropTypes.oneOf(['oneoff', 'deferred', 'makeup', 'flexi', 'paused']).isRequired,
  children: PropTypes.node.isRequired,
};

/** Circular monogram used in the compact card pile and the drawer list. */
export function Avatar({ initials, color, title, size }) {
  return (
    <span
      className={size === 'sm' ? 'cv2-avatar-sm' : 'cv2-stu__avatar'}
      style={{ background: color }}
      title={title}
    >
      {initials}
    </span>
  );
}

Avatar.propTypes = {
  initials: PropTypes.string.isRequired,
  color: PropTypes.string.isRequired,
  title: PropTypes.string,
  size: PropTypes.oneOf(['sm', 'md']),
};

Avatar.defaultProps = {
  title: undefined,
  size: 'md',
};
